import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Companies from "@/pages/Companies";
import Contacts from "@/pages/Contacts";
import Tasks from "@/pages/Tasks";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Settings from "@/pages/Settings";
import Quotations from "@/pages/Quotations";
import QuotationEditor from "@/pages/QuotationEditor";
import QuotationTemplates from "@/pages/QuotationTemplates";
import Stock from "@/pages/Stock";
import ComingSoon from "@/pages/ComingSoon";
import { Loader2 } from "lucide-react";

function FullLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#F5F7FA]">
      <Loader2 className="h-8 w-8 animate-spin text-[#0057A8]" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (!["ADMIN", "SALES_MANAGER"].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function BlockAccounts({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user?.role === "ACCOUNTS") return <Navigate to="/dashboard" replace />;
  return children;
}

function BlockAccountsWrite({ children }) {
  // Allows ACCOUNTS to view the page (list / detail) but write actions are
  // gated inside each page via the canWrite flag.
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected><AppLayout /></Protected>}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/leads/:id" element={<LeadDetail />} />
              <Route path="/companies" element={<Companies />} />
              <Route path="/companies/:id" element={<Companies />} />
              <Route path="/contacts" element={<BlockAccounts><Contacts /></BlockAccounts>} />
              <Route path="/tasks" element={<Tasks />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/settings" element={<AdminOnly><Settings /></AdminOnly>} />
              <Route path="/site-visits" element={<ComingSoon title="Site Visits" />} />
              <Route path="/quotations" element={<BlockAccountsWrite><Quotations /></BlockAccountsWrite>} />
              <Route path="/quotations/new" element={<BlockAccounts><QuotationEditor /></BlockAccounts>} />
              <Route path="/quotations/:id" element={<QuotationEditor />} />
              <Route path="/quotation-templates" element={<AdminOnly><QuotationTemplates /></AdminOnly>} />
              <Route path="/stock" element={<Stock />} />
              <Route path="/projects-soon" element={<ComingSoon title="Projects" />} />
              <Route path="/reports" element={<ComingSoon title="Reports" />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
